'use client';

import { c as _c } from "react/compiler-runtime";
import { Alert, Stack, Typography } from '@mui/material';
import type { ParameterCheckResult } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { CheckingIndicator, stepFooterButtons } from './stepCheckControls';
import { CheckResultRow } from '../CheckResultRow';
import { deriveCdcSteps, useRdsCdcStatus } from '../useRdsCdcStatus';
const parameterGroupConsoleUrl = (region: string, parameterGroupName: string) => `https://${region}.console.aws.amazon.com/rds/home?region=${region}#parameter-group-details:ids=${parameterGroupName}`;
const ParameterRow = t0 => {
  const $ = _c(20);
  const {
    param
  } = t0;
  const t1 = param.ok ? "ok" : "error";
  const t2 = param.parameterName ?? "";
  let t3;
  if ($[0] !== param.currentValue || $[1] !== param.ok || $[2] !== param.parameterGroupName) {
    t3 = param.ok && <Typography variant="body1">Value: <code>{param.currentValue}</code> on parameter group{" "}<code>{param.parameterGroupName}</code></Typography>;
    $[0] = param.currentValue;
    $[1] = param.ok;
    $[2] = param.parameterGroupName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== param.error || $[5] !== param.ok) {
    t4 = !param.ok && !!param.error && <Alert severity="error" sx={{
      marginTop: "4px"
    }}>{param.error}</Alert>;
    $[4] = param.error;
    $[5] = param.ok;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== param.currentValue || $[8] !== param.error || $[9] !== param.ok || $[10] !== param.parameterGroupName || $[11] !== param.parameterName || $[12] !== param.requiredValue) {
    t5 = !param.ok && !param.error && <Alert severity="warning" sx={{
      marginTop: "4px"
    }}><Typography variant="body2">Current: <code>{param.currentValue || "(default)"}</code>. Set{" "}<code>{param.parameterName} = {param.requiredValue}</code>{" "}in parameter group <code>{param.parameterGroupName}</code>.</Typography></Alert>;
    $[7] = param.currentValue;
    $[8] = param.error;
    $[9] = param.ok;
    $[10] = param.parameterGroupName;
    $[11] = param.parameterName;
    $[12] = param.requiredValue;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5) {
    t6 = <CheckResultRow status={t1} label={t2}>{t3}{t4}{t5}</CheckResultRow>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
export const DatabaseParametersDialog = ({
  resourceId,
  region,
  onBack,
  onClose
}: {
  resourceId: string;
  region: string;
  onBack: () => void;
  onClose: () => void;
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const {
    isLoading,
    refresh,
    parameterChecks,
    isParametersStepDone
  } = useRdsCdcStatus(resourceId);
  const parameterGroupName = parameterChecks[0]?.parameterGroupName ?? '';
  const runCheck = async (continueWhenDone: boolean) => {
    setIsChecking(true);
    setCheckError(null);
    try {
      const latest = await refresh();
      // deriveCdcSteps rather than an inline every(): an empty parameterChecks list is vacuously
      // "all ok" but means the server reported nothing, and the hub would still show the step failed.
      if (continueWhenDone && deriveCdcSteps(latest).isParametersStepDone) {
        onBack();
      }
    } catch (refreshError) {
      setCheckError(await extractErrorMessage(refreshError));
    } finally {
      setIsChecking(false);
    }
  };
  return <MainDialog isOpen={true} isLoading={isLoading} scrollableBody transitionDuration={0} header={{
    title: 'Set database parameters',
    subtitle: "These parameters must be set on the instance's parameter group before Eon can read changes.",
    onBack,
    onClose
  }} footer={{
    ...stepFooterButtons({
      isDone: isParametersStepDone,
      isBusy: isChecking,
      testIdPrefix: 'rds-cdc-parameters',
      onContinue: onBack,
      onCheckAndContinue: () => void runCheck(true),
      onVerifyAgain: () => void runCheck(false)
    }),
    children: !!region && !!parameterGroupName && <ButtonWithIcon variant='text' icon='material-symbols-open-in-new' iconPosition='right' text='Open parameter group in AWS Console' onClick={() => window.open(parameterGroupConsoleUrl(region, parameterGroupName), '_blank', 'noopener')} />
  }}>
      <Stack sx={{
      gap: '16px',
      paddingX: '40px'
    }}>
        {!isChecking && parameterChecks.length === 0 && <Alert severity='error'>
            Eon couldn&apos;t read the instance&apos;s parameter group, so there
            is nothing to check yet. Refresh and try again, or contact support
            if it keeps happening.
          </Alert>}
        {parameterChecks.map(param => <ParameterRow key={param.parameterName} param={param} />)}
        {isChecking && <CheckingIndicator text='Checking the parameter group. This might take a few seconds.' />}
        {!isChecking && isParametersStepDone && <Alert severity='success'>All required parameters are set.</Alert>}
        {!isChecking && !!checkError && <Alert severity='error'>{checkError}</Alert>}
      </Stack>
    </MainDialog>;
};